
import React, { useState } from 'react';
import { Database, Code2, Plus, Trash2, Key, Play } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';

interface DatabaseTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

interface QueryParameter {
    key: string;
    value: string;
    type: 'string' | 'number' | 'boolean' | 'json';
}

export const DatabaseTab: React.FC<DatabaseTabProps> = ({ data, onChange }) => {
    const [activeSubTab, setActiveSubTab] = useState<'config' | 'params'>('config');

    // Cast parameters to internal type QueryParameter[] as WorkflowNodeData defines it as generic string type
    const params: QueryParameter[] = (data.parameters as unknown as QueryParameter[]) || [];

    const handleAddParam = () => {
        const newParams = [...params, { key: `param_${params.length + 1}`, value: '', type: 'string' as const }];
        onChange('parameters', newParams);
    };

    const handleRemoveParam = (index: number) => {
        const newParams = params.filter((_, i) => i !== index);
        onChange('parameters', newParams);
    };

    const handleParamChange = <K extends keyof QueryParameter>(index: number, field: K, value: QueryParameter[K]) => {
        const newParams = [...params];
        newParams[index] = { ...newParams[index], [field]: value };
        onChange('parameters', newParams);
    };

    return (
        <div className="p-6 space-y-6">
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-900 flex gap-3">
                <Database className="text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-indigo-800 dark:text-indigo-300">Database Integration</p>
                    <p className="text-[11px] text-indigo-700 dark:text-indigo-400 leading-relaxed">
                        Configure SQL queries or stored procedures. Use parameters for safe variable injection.
                    </p>
                </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
                <button
                    onClick={() => setActiveSubTab('config')}
                    className={`px-4 py-2 text-xs font-bold transition-colors border-b-2 whitespace-nowrap ${activeSubTab === 'config' ? 'border-teal-500 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    Query Config
                </button>
                <button
                    onClick={() => setActiveSubTab('params')}
                    className={`px-4 py-2 text-xs font-bold transition-colors border-b-2 flex items-center gap-1 whitespace-nowrap ${activeSubTab === 'params' ? 'border-teal-500 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    Parameters
                    {params.length > 0 && <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-1.5 rounded-full text-[9px]">{params.length}</span>}
                </button>
            </div>

            {activeSubTab === 'config' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-left-2 duration-200">
                    <div className="grid grid-cols-2 gap-4">
                        <PropertyField 
                            config={{ 
                                name: 'connectionId', 
                                label: 'Connection', 
                                type: 'select', 
                                options: [
                                    { value: 'db_main', label: 'Main Postgres DB' },
                                    { value: 'db_analytics', label: 'Analytics Warehouse' },
                                    { value: 'db_users', label: 'User Directory' }
                                ],
                                defaultValue: 'db_main'
                            }}
                            value={data.connectionId}
                            onChange={onChange}
                        />
                        
                        <PropertyField 
                            config={{ 
                                name: 'queryType', 
                                label: 'Operation', 
                                type: 'select', 
                                options: [
                                    { value: 'SELECT', label: 'SELECT' },
                                    { value: 'INSERT', label: 'INSERT' },
                                    { value: 'UPDATE', label: 'UPDATE' },
                                    { value: 'DELETE', label: 'DELETE' },
                                    { value: 'PROCEDURE', label: 'Call Procedure' }
                                ],
                                defaultValue: 'SELECT'
                            }}
                            value={data.queryType}
                            onChange={onChange}
                        />
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                             <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                                <Code2 size={12}/> Query Editor
                            </label>
                            <button className="text-[10px] text-teal-600 font-bold hover:underline flex items-center gap-1">
                                <Play size={10} /> Test Query
                            </button>
                        </div>
                        <div className="relative">
                            <PropertyField 
                                config={{ 
                                    name: 'query', 
                                    label: '', 
                                    type: 'textarea', 
                                    rows: 8,
                                    placeholder: 'SELECT * FROM users WHERE status = :status AND tenant_id = :tenant',
                                    description: 'Use :paramName for parameterized queries.'
                                }}
                                value={data.query}
                                onChange={onChange}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <PropertyField 
                            config={{ 
                                name: 'resultVariable', 
                                label: 'Output Variable', 
                                type: 'text', 
                                placeholder: 'dbResult',
                                description: 'Stores query result (rows/count).'
                            }}
                            value={data.resultVariable}
                            onChange={onChange}
                        />
                        
                        <PropertyField 
                            config={{ 
                                name: 'timeout', 
                                label: 'Timeout (ms)', 
                                type: 'number', 
                                defaultValue: 30000
                            }}
                            value={data.timeout}
                            onChange={onChange}
                        />
                    </div>
                </div>
            )}

            {activeSubTab === 'params' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-2 duration-200">
                    <div className="flex justify-between items-center mb-2">
                         <div className="text-xs text-slate-500">
                             Map workflow variables to query parameters.
                         </div>
                         <button 
                            onClick={handleAddParam}
                            className="text-xs flex items-center gap-1 text-teal-600 font-bold hover:bg-teal-50 px-2 py-1 rounded transition-colors"
                         >
                             <Plus size={12} /> Add Parameter
                         </button>
                    </div>

                    <div className="space-y-2">
                        {params.map((param, index) => (
                            <div key={index} className="flex gap-2 items-start p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-800">
                                <div className="flex-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Key size={12} className="text-slate-400" />
                                        <input 
                                            type="text"
                                            value={param.key}
                                            onChange={(e) => handleParamChange(index, 'key', e.target.value)}
                                            placeholder="Param Name (e.g. status)"
                                            className="bg-transparent border-none text-xs font-bold text-slate-700 dark:text-slate-300 focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <input 
                                            type="text"
                                            value={param.value}
                                            onChange={(e) => handleParamChange(index, 'value', e.target.value)}
                                            placeholder="Value (e.g. ${status})"
                                            className="flex-1 text-xs border rounded px-2 py-1 border-slate-300 dark:border-slate-700 font-mono bg-white dark:bg-slate-800"
                                        />
                                        <select
                                            value={param.type}
                                            onChange={(e) => handleParamChange(index, 'type', e.target.value as QueryParameter['type'])}
                                            className="text-xs border rounded px-2 py-1 border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 w-20"
                                        >
                                            <option value="string">String</option>
                                            <option value="number">Number</option>
                                            <option value="boolean">Bool</option>
                                            <option value="json">JSON</option>
                                        </select>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => handleRemoveParam(index)}
                                    className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}

                        {params.length === 0 && (
                            <div className="text-center py-8 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-lg text-slate-400 text-xs">
                                No parameters defined.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
